import { create } from 'zustand';
import {
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
} from '@xyflow/react';
import type {
  Node,
  Edge,
  Connection,
  NodeChange,
  EdgeChange,
} from '@xyflow/react';
import type { NodeType, NodeConfig } from '../types';

type HistoryEntry = { nodes: Node[]; edges: Edge[] };

interface FlowBuilderState {
  // Flow data
  flowId: string | null;
  connectionId?: string;
  flowName: string;
  nodes: Node[];
  edges: Edge[];
  selectedNode: Node | null;
  isDirty: boolean;
  bitrixFields: any;

  // UI state
  isPanelOpen: boolean;
  panelMode: 'add' | 'edit' | null;

  // Actions
  setFlowId: (id: string | null) => void;
  setConnectionId: (id: string | undefined) => void;
  setFlowName: (name: string) => void;
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  setBitrixFields: (fields: any) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  addNode: (type: NodeType, position: { x: number; y: number }) => void;
  updateNodeLabel: (nodeId: string, label: string) => void;
  updateNodeConfig: (nodeId: string, config: NodeConfig) => void;
  duplicateNode: (nodeId: string, offset?: { x: number; y: number }) => void;
  deleteNode: (nodeId: string) => void;
  selectNode: (node: Node | null) => void;
  openPanel: (mode: 'add' | 'edit') => void;
  closePanel: () => void;
  setDirty: (dirty: boolean) => void;
  reset: () => void;

  // History (undo/redo) — snapshots of { nodes, edges } taken before each user mutation
  past: HistoryEntry[];
  future: HistoryEntry[];
  snapshot: (key?: string) => void;
  undo: () => void;
  redo: () => void;
  clearHistory: () => void;
}

const initialState = {
  flowId: null,
  connectionId: undefined,
  flowName: '',
  nodes: [],
  edges: [],
  selectedNode: null,
  isDirty: false,
  bitrixFields: null,
  isPanelOpen: false,
  panelMode: null as 'add' | 'edit' | null,
  past: [] as HistoryEntry[],
  future: [] as HistoryEntry[],
};

const HISTORY_LIMIT = 100;
// consecutive edits with the same key inside this window collapse into one undo step (typing)
const HISTORY_COALESCE_MS = 1200;
let lastSnapshotKey: string | null = null;
let lastSnapshotAt = 0;
let dragSnapshotTaken = false;

function cloneGraph<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

// Node type configurations
export const nodeTypeConfig: Record<
  NodeType,
  {
    label: string;
    color: string;
    icon: string;
    category: 'trigger' | 'message' | 'logic' | 'action' | 'integration';
    comingSoon?: boolean;
    hidden?: boolean;
  }
> = {
  START: { label: 'Início', color: '#22c55e', icon: 'play', category: 'trigger' },
  KEYWORD_TRIGGER: { label: 'Gatilho por Palavra', color: '#22c55e', icon: 'key', category: 'trigger' },
  SEND_MESSAGE: { label: 'Enviar Mensagem', color: '#3b82f6', icon: 'message-square', category: 'message' },
  WHISPER_MESSAGE: { label: 'Sussurro (Operador)', color: '#6366f1', icon: 'eye-off', category: 'message' },
  SEND_IMAGE: { label: 'Enviar Imagem', color: '#3b82f6', icon: 'image', category: 'message', hidden: true },
  SEND_FILE: { label: 'Enviar Arquivo', color: '#3b82f6', icon: 'file', category: 'message' },
  SEND_BUTTONS: { label: 'Pergunta', color: '#3b82f6', icon: 'help-circle', category: 'message' },
  SEND_CAROUSEL: { label: 'Carrossel de Imagens', color: '#3b82f6', icon: 'layers', category: 'message', hidden: true },
  WAIT_INPUT: { label: 'Aguardar Entrada', color: '#f59e0b', icon: 'edit', category: 'logic' },
  WAIT_CHOICE: { label: 'Aguardar Escolha', color: '#f59e0b', icon: 'list', category: 'logic' },
  CONDITION: { label: 'Condição', color: '#8b5cf6', icon: 'git-branch', category: 'logic' },
  MESSAGE_TYPE: { label: 'Tipo de Mensagem', color: '#8b5cf6', icon: 'shuffle', category: 'logic' },
  BUSINESS_HOURS: { label: 'Horário Comercial', color: '#8b5cf6', icon: 'clock', category: 'logic' },
  SWITCH: { label: 'Switch', color: '#94a3b8', icon: 'git-merge', category: 'logic', comingSoon: true },
  SET_VARIABLE: { label: 'Definir Variável', color: '#8b5cf6', icon: 'variable', category: 'logic' },
  PARSE_JSON: { label: 'Parser JSON', color: '#7c3aed', icon: 'braces', category: 'logic' },
  HTTP_REQUEST: { label: 'Requisição HTTP', color: '#06b6d4', icon: 'globe', category: 'integration' },
  DELAY: { label: 'Delay', color: '#64748b', icon: 'clock', category: 'logic' },
  AI_RESPONSE: { label: 'IA', color: '#ec4899', icon: 'sparkles', category: 'integration' },
  AI_TRANSCRIBE_AUDIO: { label: 'IA Transcrever Áudio', color: '#ec4899', icon: 'mic', category: 'integration' },
  AI_ANALYZE_IMAGE: { label: 'IA Analisar Imagem', color: '#ec4899', icon: 'scan', category: 'integration' },
  AI_INTENT: { label: 'Detectar Intenção', color: '#94a3b8', icon: 'brain', category: 'integration', comingSoon: true },
  AI_SENTIMENT: { label: 'Análise de Sentimento', color: '#94a3b8', icon: 'heart', category: 'integration', comingSoon: true },
  TRANSFER_TO_OPERATOR: { label: 'Transferir p/ Operador', color: '#ef4444', icon: 'user', category: 'action' },
  TRANSFER_TO_QUEUE: { label: 'Transferir p/ Fila', color: '#ef4444', icon: 'users', category: 'action' },
  END_SESSION: { label: 'Encerrar Sessão', color: '#ef4444', icon: 'x-circle', category: 'action' },
  CREATE_LEAD: { label: 'Criar Lead', color: '#0ea5e9', icon: 'user-plus', category: 'integration' },
  CREATE_COMPANY: { label: 'Criar Empresa', color: '#0ea5e9', icon: 'user-plus', category: 'integration' },
  CREATE_CONTACT: { label: 'Criar Contato', color: '#0ea5e9', icon: 'user-plus', category: 'integration' },
  CREATE_DEAL: { label: 'Criar Negócio', color: '#0ea5e9', icon: 'user-plus', category: 'integration' },
  GET_CRM_ENTITY: { label: 'Obter CRM', color: '#0ea5e9', icon: 'database', category: 'integration' },
  UPDATE_CONTACT: { label: 'Editar CRM', color: '#14b8a6', icon: 'user-check', category: 'integration' },
  MOVE_DEAL_STAGE: { label: 'Mover Etapa/Pipeline', color: '#0d9488', icon: 'arrow-right', category: 'integration' },
  ADD_TAG: { label: 'Captura Marketing', color: '#f97316', icon: 'tag', category: 'integration', hidden: true },
  SEARCH_CONTACT: { label: 'Buscar Cliente', color: '#f97316', icon: 'search', category: 'integration' },
  GET_EMPLOYEE_DATA: { label: 'Obter Dados do Colaborador', color: '#0ea5e9', icon: 'id-card', category: 'integration' },
  EXTRACT_DATA: { label: 'Extrair Dado', color: '#8b5cf6', icon: 'id-card', category: 'logic' },
  SUBFLOW: { label: 'Subfluxo', color: '#94a3b8', icon: 'box', category: 'logic', comingSoon: true },
};

export const useFlowBuilderStore = create<FlowBuilderState>((set, get) => ({
  ...initialState,

  setFlowId: (id) => set({ flowId: id }),
  setConnectionId: (id) => set({ connectionId: id }),
  setFlowName: (name) => set({ flowName: name }),
  setBitrixFields: (fields) => set({ bitrixFields: fields }),

  setNodes: (nodes) => set({ nodes }),
  setEdges: (edges) => set({ edges }),

  snapshot: (key) => {
    const now = Date.now();
    if (key && key === lastSnapshotKey && now - lastSnapshotAt < HISTORY_COALESCE_MS) {
      lastSnapshotAt = now; // still the same burst — keep extending it
      return;
    }
    lastSnapshotKey = key ?? null;
    lastSnapshotAt = now;
    const { nodes, edges, past } = get();
    const nextPast = [...past, { nodes: cloneGraph(nodes), edges: cloneGraph(edges) }];
    if (nextPast.length > HISTORY_LIMIT) nextPast.shift();
    set({ past: nextPast, future: [] });
  },

  undo: () => {
    const { past, future, nodes, edges, selectedNode, isPanelOpen, panelMode } = get();
    if (past.length === 0) return;
    const entry = past[past.length - 1];
    lastSnapshotKey = null;
    dragSnapshotTaken = false;
    const stillSelected = selectedNode ? entry.nodes.find((n) => n.id === selectedNode.id) || null : null;
    set({
      nodes: entry.nodes,
      edges: entry.edges,
      past: past.slice(0, -1),
      future: [...future, { nodes: cloneGraph(nodes), edges: cloneGraph(edges) }],
      selectedNode: stillSelected,
      isPanelOpen: stillSelected ? isPanelOpen : false,
      panelMode: stillSelected ? panelMode : null,
      isDirty: true,
    });
  },

  redo: () => {
    const { past, future, nodes, edges, selectedNode, isPanelOpen, panelMode } = get();
    if (future.length === 0) return;
    const entry = future[future.length - 1];
    lastSnapshotKey = null;
    dragSnapshotTaken = false;
    const stillSelected = selectedNode ? entry.nodes.find((n) => n.id === selectedNode.id) || null : null;
    set({
      nodes: entry.nodes,
      edges: entry.edges,
      past: [...past, { nodes: cloneGraph(nodes), edges: cloneGraph(edges) }],
      future: future.slice(0, -1),
      selectedNode: stillSelected,
      isPanelOpen: stillSelected ? isPanelOpen : false,
      panelMode: stillSelected ? panelMode : null,
      isDirty: true,
    });
  },

  clearHistory: () => {
    lastSnapshotKey = null;
    dragSnapshotTaken = false;
    set({ past: [], future: [] });
  },

  onNodesChange: (changes) => {
    // History: removals → snapshot; a drag → ONE snapshot at its first frame (pre-move state)
    const hasRemove = changes.some((change) => change.type === 'remove');
    const positionChanges = changes.filter((change) => change.type === 'position') as any[];
    const dragging = positionChanges.some((change) => change.dragging === true);
    const settled = positionChanges.some((change) => change.dragging !== true);
    if (hasRemove) {
      get().snapshot();
    } else if (dragging) {
      if (!dragSnapshotTaken) {
        get().snapshot();
        dragSnapshotTaken = true;
      }
    } else if (settled && !dragSnapshotTaken) {
      get().snapshot(); // discrete move (e.g. keyboard nudge)
    }
    if (settled) dragSnapshotTaken = false;

    set({
      nodes: applyNodeChanges(changes, get().nodes),
      isDirty: true,
    });
  },

  onEdgesChange: (changes) => {
    if (changes.some((change) => change.type === 'remove' || change.type === 'add' || change.type === 'replace')) {
      get().snapshot();
    }
    set({
      edges: applyEdgeChanges(changes, get().edges),
      isDirty: true,
    });
  },

  onConnect: (connection) => {
    get().snapshot();
    set({
      edges: addEdge(
        {
          ...connection,
          type: 'custom',
          animated: false,
        },
        get().edges
      ),
      isDirty: true,
    });
  },

  addNode: (type, position) => {
    get().snapshot();
    const config = nodeTypeConfig[type];
    const newNodeId = generateNodeId();
    
    const newNode: Node = {
      id: newNodeId,
      type: 'custom',
      position,
      data: {
        type,
        label: config.label,
        color: config.color,
        config: getDefaultConfig(type),
      },
    };

    set({
      nodes: [...get().nodes, newNode],
      selectedNode: newNode,
      isPanelOpen: true,
      panelMode: 'edit',
      isDirty: true,
    });
  },

  updateNodeLabel: (nodeId, label) => {
    get().snapshot(`edit:${nodeId}`);
    const currentState = get();
    const newNodes = currentState.nodes.map((node) =>
      node.id === nodeId
        ? { ...node, data: { ...node.data, label } }
        : node,
    );

    const selectedNode = currentState.selectedNode;
    const updatedSelectedNode = selectedNode?.id === nodeId
      ? newNodes.find((n) => n.id === nodeId) || null
      : selectedNode;

    set({
      nodes: newNodes,
      selectedNode: updatedSelectedNode,
      isDirty: true,
    });
  },

  updateNodeConfig: (nodeId, config) => {
    get().snapshot(`edit:${nodeId}`);
    const currentState = get();
    const previousNode = currentState.nodes.find((node) => node.id === nodeId);

    const newNodes = currentState.nodes.map((node) =>
      node.id === nodeId
        ? { ...node, data: { ...node.data, config } }
        : node,
    );

    let newEdges = currentState.edges;
    if (previousNode && ((previousNode.data as any)?.type === 'SEARCH_CONTACT')) {
      const resolveSearchIn = (cfg: any): 'deal' | 'lead' | 'contact_company' =>
        cfg?.searchIn === 'deal' ? 'deal' : cfg?.searchIn === 'lead' ? 'lead' : 'contact_company';

      const previousSearchIn = resolveSearchIn((previousNode.data as any)?.config);
      const nextSearchIn = resolveSearchIn(config as any);

      if (previousSearchIn !== nextSearchIn) {
        newEdges = remapSearchContactEdgesForMode(currentState.edges, nodeId, nextSearchIn);
      }
    }

    const selectedNode = currentState.selectedNode;
    const updatedSelectedNode = selectedNode?.id === nodeId 
      ? newNodes.find(n => n.id === nodeId) || null
      : selectedNode;

    set({
      nodes: newNodes,
      edges: newEdges,
      selectedNode: updatedSelectedNode,
      isDirty: true,
    });
  },

  duplicateNode: (nodeId, offset = { x: 40, y: 40 }) => {
    const sourceNode = get().nodes.find((node) => node.id === nodeId);
    if (!sourceNode || sourceNode.id === '__instruction_note__') {
      return;
    }
    get().snapshot();

    const newNode: Node = {
      ...sourceNode,
      id: generateNodeId(),
      position: {
        x: sourceNode.position.x + offset.x,
        y: sourceNode.position.y + offset.y,
      },
      selected: false,
      dragging: false,
      data: {
        ...(sourceNode.data as Record<string, unknown>),
        config: cloneNodeConfig((sourceNode.data as any)?.config),
      },
    };

    set({
      nodes: [...get().nodes, newNode],
      selectedNode: newNode,
      isPanelOpen: true,
      panelMode: 'edit',
      isDirty: true,
    });
  },

  deleteNode: (nodeId) => {
    get().snapshot();
    set({
      nodes: get().nodes.filter((node) => node.id !== nodeId),
      edges: get().edges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      ),
      selectedNode: get().selectedNode?.id === nodeId ? null : get().selectedNode,
      isPanelOpen: get().selectedNode?.id === nodeId ? false : get().isPanelOpen,
      isDirty: true,
    });
  },

  selectNode: (node) => {
    set({
      selectedNode: node,
      isPanelOpen: !!node,
      panelMode: node ? 'edit' : null,
    });
  },

  openPanel: (mode) => set({ isPanelOpen: true, panelMode: mode }),
  closePanel: () => set({ isPanelOpen: false, panelMode: null, selectedNode: null }),

  setDirty: (dirty) => set({ isDirty: dirty }),

  reset: () => {
    lastSnapshotKey = null;
    dragSnapshotTaken = false;
    set({ ...initialState });
  },
}));

function remapSearchContactEdgesForMode(
  edges: Edge[],
  nodeId: string,
  mode: 'contact_company' | 'deal' | 'lead',
): Edge[] {
  const remappedEdges = edges.map((edge) => {
    if (edge.source !== nodeId) {
      return edge;
    }

    const sourceHandle = edge.sourceHandle || undefined;
    if (mode === 'deal') {
      if (sourceHandle === 'contact_found' || sourceHandle === 'company_found' || sourceHandle === 'lead_found') {
        return { ...edge, sourceHandle: 'deal_found' };
      }
      return edge;
    }

    if (mode === 'lead') {
      if (sourceHandle === 'contact_found' || sourceHandle === 'company_found' || sourceHandle === 'deal_found') {
        return { ...edge, sourceHandle: 'lead_found' };
      }
      return edge;
    }

    // contact_company mode
    if (sourceHandle === 'deal_found' || sourceHandle === 'lead_found') {
      return { ...edge, sourceHandle: 'contact_found' };
    }
    return edge;
  });

  const seen = new Set<string>();
  const deduplicated: Edge[] = [];
  for (const edge of remappedEdges) {
    const dedupeKey = [
      edge.source,
      edge.sourceHandle || '',
      edge.target,
      edge.targetHandle || '',
    ].join('|');

    if (seen.has(dedupeKey)) {
      continue;
    }

    seen.add(dedupeKey);
    deduplicated.push(edge);
  }

  return deduplicated;
}

// Helper function to get default config for each node type
function getDefaultConfig(type: NodeType): NodeConfig {
  switch (type) {
    case 'START':
      return { triggers: [{ type: 'any' }] };
    case 'SEND_MESSAGE':
      return { message: '' };
    case 'WHISPER_MESSAGE':
      return { message: '' };
    case 'SEND_IMAGE':
      return { imageUrl: '', caption: '' };
    case 'SEND_FILE':
      return { fileUrl: '', fileName: '' };
    case 'SEND_BUTTONS':
      return { message: '', buttons: [], messageGroupingEnabled: false, messageGroupingWindowSeconds: 5 };
    case 'SEND_CAROUSEL':
      return { items: [] };
    case 'WAIT_INPUT':
      return { variableName: '', prompt: '', validation: null, messageGroupingEnabled: false, messageGroupingWindowSeconds: 5 };
    case 'WAIT_CHOICE':
      return { validChoices: [], errorMessage: '', messageGroupingEnabled: false, messageGroupingWindowSeconds: 5 };
    case 'CONDITION':
      return { conditions: [], defaultOutput: 'else' };
    case 'MESSAGE_TYPE':
      return {};
    case 'BUSINESS_HOURS':
      return { 
        type: 'custom',
        startTime: '09:00',
        endTime: '18:00',
        workDays: [1, 2, 3, 4, 5],
        timezone: 'America/Sao_Paulo'
      };
    case 'DELAY':
      return { duration: 1, unit: 'seconds' };
    case 'HTTP_REQUEST':
      return { method: 'GET', url: '', headers: {}, headersList: [], body: '', responseVariable: '', timeout: 30, contentType: 'application/json', authType: 'none' };
    case 'AI_RESPONSE':
      return {
        systemPrompt: '',
        model: 'gpt-6-luna',
        provider: 'openai',
        responseVariable: 'ai_response',
        responseDelaySeconds: 0,
      };
    case 'AI_TRANSCRIBE_AUDIO':
      return { provider: 'openai', model: 'gpt-transcribe', prompt: '' };
    case 'AI_ANALYZE_IMAGE':
      return { provider: 'openai', model: 'gpt-4.1-mini', imageUrlVariable: 'image_file_url', prompt: '', responseVariable: 'image_analysis' };
    case 'CREATE_COMPANY':
      return {
        mode: 'create_company',
        mappingMode: 'manual',
        jsonSourceVariable: '{{parsed_json}}',
        jsonSkipEmpty: true,
        fieldMappings: [
          {
            id: crypto.randomUUID(),
            fieldId: 'TITLE',
            value: '{{sender_name}}',
          },
        ],
        fallbackTitle: '{{sender_name}}',
        idVariable: 'created_company_id',
      };
    case 'CREATE_CONTACT':
      return {
        mode: 'create_contact',
        mappingMode: 'manual',
        jsonSourceVariable: '{{parsed_json}}',
        jsonSkipEmpty: true,
        fieldMappings: [
          {
            id: crypto.randomUUID(),
            fieldId: 'NAME',
            value: '{{sender_name}}',
          },
        ],
        fallbackTitle: '{{sender_name}}',
        idVariable: 'created_contact_id',
      };
    case 'CREATE_DEAL':
      return {
        mode: 'create_deal',
        mappingMode: 'manual',
        jsonSourceVariable: '{{parsed_json}}',
        jsonSkipEmpty: true,
        fieldMappings: [
          {
            id: crypto.randomUUID(),
            fieldId: 'TITLE',
            value: 'Negócio {{sender_name}}',
          },
        ],
        fallbackTitle: '{{sender_name}}',
        idVariable: 'created_deal_id',
      };
    case 'GET_CRM_ENTITY':
      return {
        mode: 'get_crm_entity',
        targetEntity: 'deal',
        entityId: '{{found_entity_id}}',
        fetchFieldIds: [],
        fetchFieldValueMode: 'both',
        responseVariable: 'fetched_entity',
        idVariable: 'fetched_entity_id',
      };
    case 'CREATE_LEAD':
      return {
        mode: 'create_lead',
        mappingMode: 'manual',
        jsonSourceVariable: '{{parsed_json}}',
        jsonSkipEmpty: true,
        fieldMappings: [
          {
            id: crypto.randomUUID(),
            fieldId: 'TITLE',
            value: 'Lead {{sender_name}}',
          },
          {
            id: crypto.randomUUID(),
            fieldId: 'NAME',
            value: '{{sender_name}}',
          },
        ],
        fallbackTitle: '{{sender_name}}',
        idVariable: 'created_lead_id',
      };
    case 'UPDATE_CONTACT':
      return {
        mode: 'update_crm',
        mappingMode: 'manual',
        targetEntity: 'contact',
        entityId: '{{found_entity_id}}',
        jsonSourceVariable: '{{parsed_json}}',
        jsonSkipEmpty: true,
        fieldMappings: [],
        idVariable: 'updated_entity_id',
        responseVariable: '',
      };
    case 'MOVE_DEAL_STAGE':
      return {
        mode: 'move_deal_stage',
        dealId: '{{found_deal_id}}',
        pipelineId: 'current',
        stageId: '',
        idVariable: 'moved_deal_id',
        responseVariable: '',
      };
    case 'TRANSFER_TO_OPERATOR':
      return { message: 'Transferindo para um atendente...' };
    case 'TRANSFER_TO_QUEUE':
      return { message: 'Transferindo para a fila...', queueId: '' };
    case 'END_SESSION':
      return { message: 'Atendimento encerrado. Obrigado!' };
    case 'SET_VARIABLE':
      return { variableName: '', value: '' };
    case 'PARSE_JSON':
      return {
        mode: 'parse_json',
        sourceVariable: 'ai_response',
        outputVariable: 'parsed_json',
        exposeTopLevelFields: true,
        topLevelPrefix: '',
        onError: 'fail',
        errorVariable: 'parse_json_error',
      };
    case 'ADD_TAG':
      return {
        listenEveryMessage: true,
        matchMode: 'contains',
        phoneVariable: '{{sender_phone}}',
        targetEntity: 'lead',
        dealPipelineId: 'all',
        sourceFieldId: 'SOURCE_ID',
        utmFieldMap: {
          source: 'UTM_SOURCE',
          medium: 'UTM_MEDIUM',
          campaign: 'UTM_CAMPAIGN',
          content: 'UTM_CONTENT',
          term: 'UTM_TERM',
        },
        rules: [
          {
            id: crypto.randomUUID(),
            code: '',
            sourceValue: '',
            utmSource: '',
            utmMedium: '',
            utmCampaign: '',
            utmContent: '',
            utmTerm: '',
          },
        ],
      };
    case 'SEARCH_CONTACT':
      return {
        searchField: 'phone',
        searchValue: '{{sender_phone}}',
        phoneNumber: '{{sender_phone}}',
        searchIn: 'contact_company',
        dealPipelineId: 'all',
        idVariable: 'found_entity_id',
      };
    case 'GET_EMPLOYEE_DATA':
      return {
        searchBy: 'sender',
        searchValue: '',
        fetchFieldIds: [],
        responseVariable: 'colaborador',
        idVariable: 'employee_id',
      };
    case 'EXTRACT_DATA':
      return {
        parseType: 'cpf',
        source: '{{message}}',
        outputVariable: 'extracted_value',
        docOutputFormat: 'digits',
        dateOutputFormat: 'iso',
      };
    default:
      return {};
  }
}

function generateNodeId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function cloneNodeConfig(config: unknown): NodeConfig {
  if (config === undefined) {
    return {};
  }

  if (typeof structuredClone === 'function') {
    return structuredClone(config) as NodeConfig;
  }

  return JSON.parse(JSON.stringify(config)) as NodeConfig;
}
